import { useCallback, useEffect, useState } from "react";
import { CoveragePhaseService } from "../../../../../api";
import { createNotification } from "../../../../../common/components";
import { translate } from "../../../../../common/providers";

export const useSaveLogic = ({ open, projectId, phaseId, imports, cancelTokenRef, isMountedRef }) => {
	const [saveState, setSaveState] = useState("initial");
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	useEffect(() => {
		const hasChanges = imports.length > 0;
		setHasUnsavedChanges(hasChanges);
	}, [imports]);

	const handleSave = useCallback(async () => {
		if (!hasUnsavedChanges) {
			return;
		}

		setSaveState("saving");

		try {
			const res = await CoveragePhaseService.saveImportStrategyDrafts(
				{
					projectId,
					phaseId,
					importPayloads: imports,
				},
				cancelTokenRef.current.token
			);

			if (!isMountedRef.current) {
				return;
			}

			if (res) {
				setSaveState("saved");
				setHasUnsavedChanges(false);
				createNotification({
					type: "success",
					message: translate("common.save-success"),
				});
			}
		} catch (error) {
			if (!isMountedRef.current) {
				return;
			}
			setSaveState("error");
			createNotification({
				type: "error",
				message: translate("common.save-error"),
			});
			console.error("Save error:", error);
		}
	}, [hasUnsavedChanges, projectId, phaseId, imports, cancelTokenRef, isMountedRef]);

	useEffect(() => {
		if (!open || !hasUnsavedChanges || saveState === "saving") {
			return undefined;
		}

		const autoSaveInterval = setInterval(() => {
			if (hasUnsavedChanges && saveState !== "saving" && isMountedRef.current) {
				handleSave();
			}
		}, 5000);

		return () => {
			clearInterval(autoSaveInterval);
		};
	}, [open, hasUnsavedChanges, saveState, handleSave, isMountedRef]);

	return {
		saveState,
		setSaveState,
		hasUnsavedChanges,
		setHasUnsavedChanges,
		handleSave,
	};
};
